import React, { useEffect, useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Button, Spinner, InputGroup, Form } from 'react-bootstrap';
import { FiEye, FiEdit2, FiSearch, FiList, FiGrid, FiChevronUp, FiChevronDown } from 'react-icons/fi';
import { tableAPI, recordAPI, permissionsAPI, authAPI } from '../api/api';
import { AuthContext } from '../context/AuthContext';
import './TablesList.css';

function TablesList() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [tables, setTables] = useState([]);
  const [filteredTables, setFilteredTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [tablePermissions, setTablePermissions] = useState({});
  const [isAdmin, setIsAdmin] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  const [sortColumn, setSortColumn] = useState('tableName');
  const [sortDirection, setSortDirection] = useState('asc');

  useEffect(() => {
    if (user) {
      // Fetch permissions first, then fetch tables
      (async () => {
        const isAdminUser = await fetchTablePermissions();
        await fetchTables(isAdminUser);
      })();
    }
  }, [user]);

  const fetchTablePermissions = async () => {
    try {
      const sessionRes = await authAPI.checkSession();
      if (sessionRes.data.success && sessionRes.data.user) {
        const userData = sessionRes.data.user;
        const userRole = userData.Role || userData.role || '';
        
        // Admin users have access to all tables
        if (userRole.toLowerCase() === 'admin') {
          setIsAdmin(true);
          return true; // Return true to indicate admin user
        }

        const groupIds = userData.groupIds || [userData.groupId];
        const groupId = groupIds[0];

        if (groupId) {
          const permRes = await permissionsAPI.getGroupPermissions(groupId);
          if (permRes.data.success && permRes.data.tablePermissions) {
            // Build a map of table names to their view permissions
            const perms = {};
            permRes.data.tablePermissions.forEach(tp => {
              perms[tp.TableName] = {
                CanView: tp.CanView === 1 || tp.CanView === true
              };
            });
            setTablePermissions(perms);
          }
        }
      } else {
        setError('Session expired. Please login again.');
        return false;
      }
      return false; // Not admin
    } catch (error) {
      setError('Error checking session: ' + error.message);
      return false;
    }
  };

  const fetchTables = async (isAdminUser = false) => {
    try {
      setLoading(true);
      const response = await tableAPI.getAllTables();

      if (response.data.success) {
        let allTables = response.data.tables || [];

        if (allTables.length === 0) {
          setError('No tables available. Please contact administrator if this is unexpected.');
          setTables([]);
          setFilteredTables([]);
          return;
        }
        
        // Filter tables based on user permissions (for non-admin client users)
        if (user && user.clientId && !isAdminUser) {
          allTables = allTables.filter(table => {
            return tablePermissions[table.tableName] && tablePermissions[table.tableName].CanView;
          });
        }
        
        const tablesWithCounts = await Promise.all(
          allTables.map(async (table) => {
            try {
              const countResponse = await recordAPI.getRecordCount(table.tableName);
              const count = countResponse.data.success ? countResponse.data.data.total : 0;
              return { ...table, recordCount: count };
            } catch (err) {
              return { ...table, recordCount: 0 };
            }
          })
        );
        setTables(tablesWithCounts);
        setFilteredTables(tablesWithCounts);
        setError(null); // Clear any previous errors
      } else {
        const errorMsg = response.data.message || 'Failed to load tables';
        setError(errorMsg);
      }
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.message || 'Failed to load tables';
      setError('Error loading tables: ' + errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    const query = e.target.value.toLowerCase();
    setSearchQuery(query);
    
    if (query === '') {
      setFilteredTables(tables);
    } else {
      const filtered = tables.filter(table => {
        const tableName = table.tableName || '';
        const displayName = table.name || table.tableName || '';
        return tableName.toLowerCase().includes(query) || 
               displayName.toLowerCase().includes(query);
      });
      setFilteredTables(filtered);
    }
  };

  const handleSort = (column) => {
    let direction = 'asc';
    if (sortColumn === column && sortDirection === 'asc') {
      direction = 'desc';
    }
    setSortColumn(column);
    setSortDirection(direction);

    const sorted = [...filteredTables].sort((a, b) => {
      let aValue, bValue;

      if (column === 'tableName') {
        aValue = a.tableName || '';
        bValue = b.tableName || '';
      } else if (column === 'recordCount') {
        aValue = a.recordCount || 0;
        bValue = b.recordCount || 0;
      }

      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
        return direction === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
      } else {
        return direction === 'asc' ? aValue - bValue : bValue - aValue;
      }
    });

    setFilteredTables(sorted);
  };

  const renderSortIcon = (column) => {
    if (sortColumn !== column) return null;
    return sortDirection === 'asc' ? (
      <FiChevronUp className="sort-icon" />
    ) : (
      <FiChevronDown className="sort-icon" />
    );
  };

  const handleOpenTable = (tableName) => {
    navigate(`/tables/${tableName}`);
  };

  if (loading) {
    return (
      <Container fluid className="tables-list">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading tables...</span>
          </Spinner>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="tables-list">
      {isAdmin && (
        <div className="tables-header">
          <h2>Available Tables</h2>
          <p className="tables-count">Total: {tables.length} tables</p>
        </div>
      )}

      {error && <div className="alert alert-danger">{error}</div>}

      <div className="search-section mb-4">
        <Row className="align-items-center">
          <Col md={9}>
            <InputGroup>
              <InputGroup.Text className="bg-light">
                <FiSearch />
              </InputGroup.Text>
              <Form.Control
                placeholder="Search tables by name..."
                value={searchQuery}
                onChange={handleSearch}
              />
            </InputGroup>
          </Col>
          <Col md={3}>
            <div className="view-mode-toggle">
              <Button
                variant={viewMode === 'grid' ? 'primary' : 'outline-primary'}
                size="sm"
                onClick={() => setViewMode('grid')}
                className="me-2"
                title="Grid view"
              >
                <FiGrid /> Grid
              </Button>
              <Button
                variant={viewMode === 'list' ? 'primary' : 'outline-primary'}
                size="sm"
                onClick={() => setViewMode('list')}
                title="List view"
              >
                <FiList /> List
              </Button>
            </div>
          </Col>
        </Row>
      </div>

      {filteredTables.length === 0 ? (
        <div className="alert alert-info">
          {searchQuery ? 'No tables found matching your search' : 'No tables available'}
        </div>
      ) : viewMode === 'grid' ? (
        <div className="tables-grid">
          {filteredTables.map(table => (
            <div key={table.tableName} className="table-grid-card">
              <div className="card-header">
                <h5 className="card-title" title={table.tableName}>{table.tableName}</h5>
              </div>
              <div className="card-body">
                <div className="card-field">
                  <span className="field-label">Record Count:</span>
                  <span className="field-value">{table.recordCount || 0}</span>
                </div>
              </div>
              <div className="card-footer">
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => handleOpenTable(table.tableName)}
                  className="view-records-btn"
                  title="View and edit records"
                >
                  <FiEye className="me-1" />
                  View Records
                </Button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="tables-list-view">
          <table className="table table-hover">
            <thead>
              <tr>
                <th 
                  className="sortable-header"
                  onClick={() => handleSort('tableName')}
                  title="Click to sort"
                >
                  Table Name
                  {renderSortIcon('tableName')}
                </th>
                <th 
                  className="text-center sortable-header"
                  onClick={() => handleSort('recordCount')}
                  title="Click to sort"
                >
                  Records
                  {renderSortIcon('recordCount')}
                </th>
                <th className="text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredTables.map(table => (
                <tr key={table.tableName}>
                  <td className="table-name-cell">{table.tableName}</td>
                  <td className="text-center">
                    <span className="badge bg-info">{table.recordCount || 0}</span>
                  </td>
                  <td className="text-center">
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => handleOpenTable(table.tableName)}
                      title="View and edit records"
                    >
                      <FiEye className="me-1" />
                      View
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="tables-footer mt-5 mb-4">
        <p className="text-muted">
          Showing {filteredTables.length} of {tables.length} tables
        </p>
      </div>
    </Container>
  );
}

export default TablesList;
